/**
 * ACL (Access Control List) Logic
 * ระบบตรวจสอบสิทธิ์การเข้าถึง
 *
 * Shared across features — used by router, auth hooks, and UI guards.
 * ใช้ร่วมกันข้ามฟีเจอร์ — ใช้โดย router, auth hooks, และ UI guards
 */

/**
 * Check if user has a specific permission.
 * Supports wildcards: 'companies:*' matches 'companies:read'.
 * ตรวจสอบว่าผู้ใช้มีสิทธิ์ที่ระบุหรือไม่ รองรับ wildcard
 *
 * @param userPermissions - list of permissions the user has
 * @param requiredPermission - the permission to check
 * @returns true if user has the required permission
 */
export const hasPermission = (userPermissions: string[], requiredPermission: string): boolean => {
  // Admin with global wildcard
  if (userPermissions.includes('*')) return true

  // Direct match
  if (userPermissions.includes(requiredPermission)) return true

  // Wildcard match (e.g. 'companies:*' matches 'companies:read')
  const [resource, action] = requiredPermission.split(':')
  if (!resource || !action) return false

  return userPermissions.some((perm) => {
    const [permResource, permAction] = perm.split(':')
    return (
      (permResource === '*' || permResource === resource) &&
      (permAction === '*' || permAction === action)
    )
  })
}

/**
 * Check if user has ANY of the required permissions.
 * ตรวจสอบว่าผู้ใช้มีสิทธิ์อย่างน้อยหนึ่งรายการ
 */
export const hasAnyPermission = (
  userPermissions: string[],
  requiredPermissions: string[]
): boolean => {
  return requiredPermissions.some((perm) => hasPermission(userPermissions, perm))
}

/**
 * Check if user has ALL of the required permissions.
 * ตรวจสอบว่าผู้ใช้มีสิทธิ์ทั้งหมดที่ระบุ
 */
export const hasAllPermissions = (
  userPermissions: string[],
  requiredPermissions: string[]
): boolean => {
  return requiredPermissions.every((perm) => hasPermission(userPermissions, perm))
}
